<template>
  <div class="vertical-sections-container">
    <!-- Loop through each section -->
    <div
      v-for="(section, index) in sections"
      :key="index"
      class="section-block"
    >
      <!-- Section Header -->
      <div class="section-header">
        <i v-if="section.icon" :class="section.icon"></i>
        <h2 class="section-title">{{ section.title }}</h2>
      </div>

      <!-- 2x2 Card Grid -->
      <div v-if="section.type === 'card-grid'" class="card-grid-2x2">
        <div v-for="(card, idx) in section.cards" :key="idx" class="grid-card">
          <div class="card-icon-wrapper">
            <i v-if="card.icon" :class="card.icon"></i>
          </div>
          <h3 class="card-title">{{ card.title }}</h3>
          <p class="card-description">{{ card.description }}</p>
          <ul v-if="card.items" class="card-items">
            <li v-for="(item, i) in card.items" :key="i">{{ item }}</li>
          </ul>
        </div>
      </div>

      <!-- Horizontal Tool Badges with Auto-Slide -->
      <div v-else-if="section.type === 'tool-badges'" class="tool-badges-container">
        <div class="tools-slider-wrapper">
          <div class="tools-slider">
            <!-- Duplicate tools for seamless loop -->
            <div class="tools-track">
              <div
                v-for="(tool, idx) in getAllTools(section.categories)"
                :key="`tool-${idx}`"
                class="tool-item"
              >
                <div class="tool-logo-wrapper">
                  <img v-if="tool.logo" :src="tool.logo" :alt="tool.name" class="tool-logo" />
                  <i v-else-if="tool.icon" :class="tool.icon" class="tool-icon-fallback"></i>
                </div>
                <span class="tool-name">{{ tool.name }}</span>
              </div>
              <!-- Duplicate for seamless loop -->
              <div
                v-for="(tool, idx) in getAllTools(section.categories)"
                :key="`tool-dup-${idx}`"
                class="tool-item"
              >
                <div class="tool-logo-wrapper">
                  <img v-if="tool.logo" :src="tool.logo" :alt="tool.name" class="tool-logo" />
                  <i v-else-if="tool.icon" :class="tool.icon" class="tool-icon-fallback"></i>
                </div>
                <span class="tool-name">{{ tool.name }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
interface Card {
  icon?: string
  title: string
  description: string
  items?: string[]
}

interface Tool {
  icon?: string
  logo?: string
  name: string
}

interface ToolCategory {
  name?: string
  tools: Tool[]
}

interface Section {
  title: string
  icon?: string
  type: 'card-grid' | 'tool-badges'
  cards?: Card[]
  categories?: ToolCategory[]
}

defineProps<{
  sections: Section[]
}>()

// Helper function to get all tools from all categories
const getAllTools = (categories?: ToolCategory[]): Tool[] => {
  if (!categories) return []
  return categories.flatMap(category => category.tools)
}
</script>

<style scoped>
.vertical-sections-container {
  padding: 0;
}

.section-block {
  margin-bottom: 48px;
}

.section-block:last-child {
  margin-bottom: 0;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
  padding-bottom: 12px;
  border-bottom: 2px solid rgba(139, 92, 246, 0.2);
}

.section-header i {
  font-size: 28px;
  color: #3b82f6;
}

.section-title {
  font-size: 24px;
  font-weight: 700;
  color: #1E293B;
  margin: 0;
}

/* 2x2 Card Grid */
.card-grid-2x2 {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.grid-card {
  background: white;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(139, 92, 246, 0.1);
  transition: all 0.3s ease;
}

.grid-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 16px rgba(139, 92, 246, 0.15);
  border-color: rgba(139, 92, 246, 0.3);
}

.card-icon-wrapper {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
}

.card-icon-wrapper i {
  font-size: 20px;
  color: white;
}

.card-title {
  font-size: 16px;
  font-weight: 700;
  color: #1E293B;
  margin: 0 0 8px 0;
  line-height: 1.3;
}

.card-description {
  font-size: 13px;
  color: #64748B;
  margin: 0 0 12px 0;
  line-height: 1.5;
}

.card-items {
  list-style: none;
  padding: 0;
  margin: 0;
}

.card-items li {
  font-size: 12px;
  color: #475569;
  padding: 5px 0;
  padding-left: 18px;
  position: relative;
  line-height: 1.4;
}

.card-items li::before {
  content: '•';
  position: absolute;
  left: 0;
  color: #3b82f6;
  font-weight: bold;
  font-size: 14px;
}

/* Tool Badges - Auto Sliding */
.tool-badges-container {
  padding: 24px 0;
  overflow: hidden;
}

.tools-slider-wrapper {
  width: 100%;
  overflow: hidden;
  position: relative;
  background: linear-gradient(90deg,
    rgba(255,255,255,0) 0%,
    rgba(255,255,255,0.5) 5%,
    rgba(255,255,255,0.5) 95%,
    rgba(255,255,255,0) 100%);
  padding: 20px 0;
}

.tools-slider {
  overflow: hidden;
}

.tools-track {
  display: flex;
  gap: 32px;
  animation: slideLeft 40s linear infinite;
  width: fit-content;
}

.tools-track:hover {
  animation-play-state: paused;
}

@keyframes slideLeft {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-50%);
  }
}

.tool-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: white;
  border-radius: 12px;
  border: 2px solid rgba(139, 92, 246, 0.1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  min-width: 120px;
  transition: all 0.3s ease;
}

.tool-item:hover {
  transform: translateY(-4px);
  border-color: rgba(139, 92, 246, 0.3);
  box-shadow: 0 8px 16px rgba(139, 92, 246, 0.2);
}

.tool-logo-wrapper {
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, rgba(139, 92, 246, 0.05), rgba(168, 85, 247, 0.05));
  border-radius: 12px;
  padding: 8px;
}

.tool-logo {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.tool-icon-fallback {
  font-size: 32px;
  color: #3b82f6;
}

.tool-name {
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  text-align: center;
  line-height: 1.2;
}

/* Responsive */
@media (max-width: 768px) {
  .card-grid-2x2 {
    grid-template-columns: 1fr;
  }

  .section-header i {
    font-size: 24px;
  }

  .section-title {
    font-size: 20px;
  }

  .section-block {
    margin-bottom: 40px;
  }
}
</style>
